// PortfolioCommentary (TASK-581) — the grounded portfolio narrative the
// generate_portfolio_analysis skill writes (real figures, deterministic
// suitability/ESG/concentration flags, since-last-time delta), rendered as a
// canvas tile: prose + flag chips + delta line. Self-fetches by clientId via
// portfolioApiService; a miss auto-generates server-side, so the widget polls
// while status === "generating" (capped) and shows an honest empty state
// otherwise — never fake-green.
import { useQuery } from '@tanstack/react-query';
import {
  portfolioApiService,
  type PortfolioAnalysisFlag,
  type PortfolioAnalysisLatest,
} from '../../../services/api/portfolioApiService';
import { WidgetContainer } from '../WidgetContainer';

const POLL_MS = 5000;
const POLL_CAP_MS = 90_000; // generation is one LLM call; give up politely after this

const FLAG_LABEL: Record<string, string> = {
  suitability_drift: 'Suitability',
  esg_mismatch: 'ESG match',
  concentration: 'Concentration',
};

// status → chip tone. "unknown" is a source gap, styled neutral — not green.
const STATUS_CLASS: Record<string, string> = {
  ok: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  drift: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  flag: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  breach: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  mismatch: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  unknown: 'bg-accent text-muted-foreground',
};

function FlagChip({ id, flag }: { id: string; flag?: PortfolioAnalysisFlag }) {
  if (!flag) return null;
  const tone = STATUS_CLASS[flag.status] ?? STATUS_CLASS.unknown;
  const extra =
    flag.items?.length
      ? ` · ${flag.items[0].name} ${flag.items[0].weight_pct}%`
      : flag.conflicts?.length
        ? ` · ${flag.conflicts[0]}`
        : '';
  return (
    <span
      title={flag.detail}
      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}
    >
      {FLAG_LABEL[id]}: {flag.status}
      {extra}
    </span>
  );
}

export function PortfolioCommentary({ clientId }: { clientId: string }) {
  const startedAt = Date.now();
  const { data, isLoading, isError } = useQuery<PortfolioAnalysisLatest>({
    queryKey: ['portfolio-commentary', clientId],
    queryFn: () => portfolioApiService.getLatestPortfolioAnalysis(clientId),
    enabled: !!clientId,
    refetchInterval: (query) => {
      const latest = query.state.data;
      const generating = latest?.status === 'generating' && !latest?.analysis;
      return generating && Date.now() - startedAt < POLL_CAP_MS ? POLL_MS : false;
    },
  });

  const analysis = data?.analysis ?? null;
  const flags = analysis?.flags ?? null;
  const deltaChanges = analysis?.delta?.changes ?? [];

  return (
    <WidgetContainer
      title="Portfolio commentary"
      source="Portfolio · AI analysis"
      // TASK-825: the analysis's own timestamp.
      dataAsOf={analysis?.generated_at ?? null}
    >
      {isLoading && <div className="h-24 w-full animate-pulse rounded bg-accent" />}
      {isError && (
        <div className="text-[13px] text-muted-foreground">
          Could not load the portfolio analysis.
        </div>
      )}
      {!isLoading && !isError && !analysis && data?.status === 'generating' && (
        <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground/40 border-t-transparent" />
          Generating the portfolio analysis from live figures…
        </div>
      )}
      {!isLoading && !isError && !analysis && data?.status !== 'generating' && (
        <div className="text-[13px] text-muted-foreground">
          No portfolio analysis available for this client — no portfolio data was found to
          analyze.
        </div>
      )}
      {analysis && (
        <div>
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            {(['suitability_drift', 'esg_mismatch', 'concentration'] as const).map((k) => (
              <FlagChip key={k} id={k} flag={flags?.[k]} />
            ))}
            {typeof analysis.confidence_score === 'number' && (
              <span className="ml-auto rounded-full bg-accent px-2 py-0.5 text-[12px] font-medium text-foreground">
                {Math.round(analysis.confidence_score * 100)}% confidence
              </span>
            )}
          </div>

          <p className="whitespace-pre-line text-[13px] text-muted-foreground">
            {analysis.portfolio_analysis}
          </p>

          <div className="mt-3 border-t border-border pt-2 text-[11px] text-muted-foreground">
            {deltaChanges.length > 0 ? (
              <span>Since last analysis: {deltaChanges.join(' · ')}</span>
            ) : analysis.delta ? (
              <span>Since last analysis: no material change</span>
            ) : (
              <span>First analysis for this client</span>
            )}
            {analysis.generated_at &&
              ` · ${new Date(analysis.generated_at).toLocaleDateString()}`}
          </div>
        </div>
      )}
    </WidgetContainer>
  );
}
